import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Home, NavigateNext, PhotoCamera, Save, Visibility } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const STATUS_TRIPLA = [
  { value: 'ok', label: 'OK' },
  { value: 'parado', label: 'PARADO' },
  { value: 'nao_existe', label: 'NÃO EXISTE' },
];

const STATUS_BINARIO = [
  { value: 'operando', label: 'OPERANDO' },
  { value: 'inoperante', label: 'INOPERANTE' },
];

const STATUS_LONA = [
  { value: 'sem_rasgo', label: 'SEM RASGO' },
  { value: 'com_rasgo', label: 'COM RASGO' },
];

const STATUS_BATENTE = [
  { value: 'sem_amassados', label: 'SEM AMASSADOS' },
  { value: 'com_amassados', label: 'COM AMASSADOS' },
];

const STATUS_EXISTENCIA = [
  { value: 'existente', label: 'EXISTENTE' },
  { value: 'nao_existente', label: 'NÃO EXISTENTE' },
];

const STATUS_DIFUSO = [
  { value: 'operando', label: 'OPERANDO' },
  { value: 'inoperante', label: 'INOPERANTE' },
  { value: 'nao_existente', label: 'NÃO EXISTENTE' },
];

const REFRIGERACAO_ITEMS = [
  'CASA DE MÁQUINAS',
  'BALCÃO AÇOUGUE',
  'BALCÃO PADARIA',
  'BALCÃO EMPÓRIO',
  'BALCÃO LATICÍNIOS',
  'BALCÃO FLV',
  'BALCÃO AUTO-SERVIÇO AÇOUGUE',
  'BALCÃO CHOPP',
  'ILHAS SELFIE',
  'CÂMARA CONGELADA',
  'CÂMARA RESFRIADA',
  'ANTE CÂMARA',
  'CÂMARA BOVINOS',
  'CÂMARA SUÍNOS',
  'CÂMARA FRANGOS',
  'CÂMARA SEBO E OSSO',
];

const AR_CONDICIONADO_ITEMS = [
  'SPLITÃO 1',
  'SPLITÃO 2',
  'SPLITÃO 3',
  'SPLIT GERÊNCIA',
  'SPLIT TESOURARIA',
  'SPLIT SALA DE DADOS',
  'SPLIT MONITORAMENTO',
  'SPLIT SALA DE CARTAZ',
  'SPLIT CPD',
  'SPLIT SALA DE DESCANSO 1',
  'SPLIT SALA DE DESCANSO 2',
  'SPLIT/PISO TETO REFEITÓRIO 1',
  'SPLIT/PISO TETO REFEITÓRIO 2',
  'SPLIT/PISO TETO REFEITÓRIO 3',
  'SPLIT SALA COFRE',
  'SPLIT SALA IFOOD',
];

const PORTA_FIELDS = [
  { key: 'status', label: 'PORTA CÂMARA', options: STATUS_BINARIO },
  { key: 'lona', label: 'LONA', options: STATUS_LONA },
  { key: 'batente', label: 'BATENTE', options: STATUS_BATENTE },
  { key: 'sensor_fotocelula', label: 'SENSOR FOTOCÉLULA', options: STATUS_BINARIO },
  { key: 'sensor_difuso', label: 'SENSOR DIFUSO', options: STATUS_DIFUSO },
  { key: 'pirulitos_protecao', label: 'PIRULITOS DE PROTEÇÃO', options: STATUS_EXISTENCIA },
  { key: 'botoeira_interna', label: 'BOTOEIRA INTERNA', options: STATUS_BINARIO },
  { key: 'botoeira_externa', label: 'BOTOEIRA EXTERNA', options: STATUS_BINARIO },
];

const toLocalDateTimeValue = (date = new Date()) => {
  const offset = date.getTimezoneOffset() * 60000;
  const local = new Date(date.getTime() - offset);
  return local.toISOString().slice(0, 16);
};

const buildInitialMap = (items) =>
  items.reduce((acc, item) => {
    acc[item] = '';
    return acc;
  }, {});

const buildInitialPorta = () =>
  PORTA_FIELDS.reduce((acc, field) => {
    acc[field.key] = '';
    return acc;
  }, {});

const buildInitialDetalhado = () => ({
  refrigeracao: buildInitialMap(REFRIGERACAO_ITEMS),
  ar_condicionado: buildInitialMap(AR_CONDICIONADO_ITEMS),
  gerador: {
    tipo_teste: '',
    partida: '',
    ruido: '',
    nivel_comb_tanque: '',
    nivel_comb_reservatorio: '',
    nivel_agua: '',
    nivel_oleo_motor: '',
  },
  portas_rapidas: {
    resfriada: buildInitialPorta(),
    congelada: buildInitialPorta(),
  },
  observacoes: {
    refrigeracao: '',
    ar_condicionado: '',
    gerador: '',
    portas_resfriada: '',
    portas_congelada: '',
  },
});

const countFilledValues = (value) => {
  if (Array.isArray(value)) {
    return value.reduce((sum, item) => sum + countFilledValues(item), 0);
  }
  if (!value || typeof value !== 'object') {
    return value ? 1 : 0;
  }
  return Object.values(value).reduce((sum, item) => sum + countFilledValues(item), 0);
};

export default function TestesChecklistNovo() {
  const navigate = useNavigate();
  const { token, user } = useAuth();

  const [clientes, setClientes] = useState([]);
  const [clienteId, setClienteId] = useState('');
  const [loja, setLoja] = useState('');
  const [dataChecklist, setDataChecklist] = useState(toLocalDateTimeValue());
  const [detalhado, setDetalhado] = useState(buildInitialDetalhado);
  const [observacoesGerais, setObservacoesGerais] = useState('');
  const [tabAtiva, setTabAtiva] = useState(0);
  const [fotos, setFotos] = useState([]);
  const [itemFotos, setItemFotos] = useState({});
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const fotoInputRef = useRef(null);

  const headers = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  useEffect(() => {
    const carregarClientes = async () => {
      try {
        const response = await fetch('/api/clientes', { headers });
        if (!response.ok) return;
        const data = await response.json();
        const lista = Array.isArray(data) ? data : [];
        setClientes(lista);
      } catch {
        setClientes([]);
      }
    };

    if (token) carregarClientes();
  }, [headers, token]);

  const handleFotosChange = (event) => {
    const files = Array.from(event.target.files || []);
    setFotos((prev) => {
      const remaining = 10 - prev.length;
      if (remaining <= 0) {
        setFeedback({ type: 'warning', message: 'Limite de 10 fotos atingido.' });
        return prev;
      }
      const allowed = files.slice(0, remaining);
      if (files.length > remaining) {
        setFeedback({ type: 'warning', message: `Apenas ${remaining} foto(s) adicionada(s). Limite de 10 atingido.` });
      }
      return [...prev, ...allowed];
    });
    event.target.value = '';
  };

  const removerFoto = (index) => {
    setFotos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleItemFotosChange = (itemKey, event) => {
    const selected = Array.from(event.target.files || []);
    if (!selected.length) return;
    setItemFotos((prev) => ({
      ...prev,
      [itemKey]: [...(prev[itemKey] || []), ...selected],
    }));
    event.target.value = '';
  };

  const handleClienteChange = (value) => {
    setClienteId(value);
    const clienteSelecionado = clientes.find((item) => String(item.id) === String(value));
    if (clienteSelecionado?.nome) {
      setLoja(clienteSelecionado.nome);
    } else {
      setLoja('');
    }
  };

  const handleRefrigeracaoChange = (item, value) => {
    setDetalhado((prev) => ({
      ...prev,
      refrigeracao: { ...prev.refrigeracao, [item]: value },
    }));
  };

  const handleArChange = (item, value) => {
    setDetalhado((prev) => ({
      ...prev,
      ar_condicionado: { ...prev.ar_condicionado, [item]: value },
    }));
  };

  const handleGeradorChange = (field, value) => {
    setDetalhado((prev) => ({
      ...prev,
      gerador: { ...prev.gerador, [field]: value },
    }));
  };

  const handlePortaChange = (tipo, field, value) => {
    setDetalhado((prev) => ({
      ...prev,
      portas_rapidas: {
        ...prev.portas_rapidas,
        [tipo]: {
          ...prev.portas_rapidas[tipo],
          [field]: value,
        },
      },
    }));
  };

  const handleObservacaoSecaoChange = (secao, value) => {
    setDetalhado((prev) => ({
      ...prev,
      observacoes: {
        ...prev.observacoes,
        [secao]: value,
      },
    }));
  };

  const uploadFile = async (entidade, file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`/api/uploads/${entidade}`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Falha no upload de arquivo.');
    }

    return response.json();
  };

  const handleSalvar = async () => {
    if (!clienteId) {
      setFeedback({ type: 'error', message: 'Selecione o cliente antes de salvar.' });
      return;
    }

    const totalRespondido = countFilledValues(detalhado);
    if (totalRespondido === 0) {
      setFeedback({
        type: 'error',
        message: 'Preencha ao menos um item do checklist antes de salvar.',
      });
      return;
    }

    setSaving(true);
    setFeedback({ type: '', message: '' });

    try {
      const checklistId = `${Date.now()}`;
      const uploadedPhotos = [];
      const uploadedItemPhotos = {};

      for (let index = 0; index < fotos.length; index += 1) {
        const foto = fotos[index];
        const safeName = (foto.name || `foto_${index + 1}`).replace(/\s+/g, '_');
        const fileName = `checklist_${checklistId}_foto_${index + 1}_${safeName}`;
        const renamed = new File([foto], fileName, { type: foto.type || 'application/octet-stream' });
        const uploadResult = await uploadFile('testes_checklist', renamed);
        uploadedPhotos.push({
          nome: uploadResult.filename,
          upload_id: uploadResult.upload_id,
          tamanho: foto.size,
        });
      }

      for (const [itemKey, files] of Object.entries(itemFotos)) {
        uploadedItemPhotos[itemKey] = [];
        for (let index = 0; index < files.length; index += 1) {
          const foto = files[index];
          const safeKey = itemKey.replace(/[^a-zA-Z0-9_-]+/g, '_');
          const safeName = (foto.name || `foto_item_${index + 1}`).replace(/\s+/g, '_');
          const fileName = `checklist_${checklistId}_${safeKey}_foto_${index + 1}_${safeName}`;
          const renamed = new File([foto], fileName, { type: foto.type || 'application/octet-stream' });
          const uploadResult = await uploadFile('testes_checklist', renamed);
          uploadedItemPhotos[itemKey].push({
            nome: uploadResult.filename,
            upload_id: uploadResult.upload_id,
            tamanho: foto.size,
          });
        }
      }

      const payload = {
        versao: 2,
        checklist_id: checklistId,
        cliente_id: Number(clienteId) || null,
        cliente_nome: clientes.find((item) => String(item.id) === String(clienteId))?.nome || '',
        loja: loja.trim() || clientes.find((item) => String(item.id) === String(clienteId))?.nome || '',
        data_checklist: new Date(dataChecklist).toISOString(),
        checklist_detalhado: detalhado,
        observacoes: observacoesGerais.trim(),
        fotos: uploadedPhotos,
        item_fotos: uploadedItemPhotos,
        criado_por: {
          id: user?.id || null,
          nome: user?.nome || user?.username || 'Usuário',
        },
        criado_em: new Date().toISOString(),
      };

      const jsonBlob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const jsonFile = new File([jsonBlob], `checklist_${checklistId}.json`, { type: 'application/json' });
      await uploadFile('testes_checklist', jsonFile);

      setFeedback({ type: 'success', message: 'Checklist completo salvo com sucesso.' });
      setClienteId('');
      setLoja('');
      setDataChecklist(toLocalDateTimeValue());
      setDetalhado(buildInitialDetalhado());
      setObservacoesGerais('');
      setFotos([]);
      setItemFotos({});
    } catch (error) {
      setFeedback({
        type: 'error',
        message: error?.message || 'Não foi possível salvar o checklist.',
      });
    } finally {
      setSaving(false);
    }
  };

  const renderCameraButton = (itemKey) => {
    const totalFotos = itemFotos[itemKey]?.length || 0;
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Tooltip title="Anexar foto deste item">
          <IconButton component="label" size="small" color={totalFotos ? 'primary' : 'default'}>
            <PhotoCamera fontSize="small" />
            <input hidden type="file" accept="image/*" multiple onChange={(e) => handleItemFotosChange(itemKey, e)} />
          </IconButton>
        </Tooltip>
        {totalFotos ? <Chip size="small" color="primary" label={totalFotos} /> : null}
      </Stack>
    );
  };

  const getOptionColor = (value) => {
    if (value === 'ok' || value === 'operando' || value === 'sem_rasgo' || value === 'sem_amassados' || value === 'existente') {
      return 'success';
    }
    if (value === 'parado' || value === 'inoperante' || value === 'com_rasgo' || value === 'com_amassados' || value === 'nao_partiu' || value === 'ruido_anormal' || value === 'baixo') {
      return 'error';
    }
    if (value === 'nao_existe' || value === 'nao_existente' || value === 'medio') {
      return 'warning';
    }
    return 'primary';
  };

  const renderMobileOptionButtons = (value, onChange, options) => (
    <Grid container spacing={1}>
      {options.map((option) => {
        const selected = value === option.value;
        return (
          <Grid item xs={4} key={`mobile-${option.value}`}>
            <Button
              fullWidth
              size="small"
              color={getOptionColor(option.value)}
              variant={selected ? 'contained' : 'outlined'}
              onClick={() => onChange(selected ? '' : option.value)}
              sx={{
                minHeight: 42,
                fontSize: '0.7rem',
                fontWeight: 800,
                whiteSpace: 'normal',
                lineHeight: 1.1,
              }}
            >
              {option.label}
            </Button>
          </Grid>
        );
      })}
    </Grid>
  );

  const renderStatusCheckbox = (label, value, onChange, options, itemKey = label) => (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1}
      alignItems={{ xs: 'flex-start', md: 'center' }}
      sx={{ py: 0.5 }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: { md: 300 } }}>
        {renderCameraButton(itemKey)}
        <Typography sx={{ fontWeight: 500, fontSize: '0.92rem' }}>{label}:</Typography>
      </Stack>
      <Box sx={{ display: { xs: 'block', md: 'none' }, width: '100%' }}>
        {renderMobileOptionButtons(value, onChange, options)}
      </Box>
      <Stack direction="row" spacing={0.4} useFlexGap flexWrap="wrap" sx={{ display: { xs: 'none', md: 'flex' } }}>
        {options.map((option) => {
          const checked = value === option.value;
          return (
            <FormControlLabel
              key={`${label}-${option.value}`}
              control={
                <Checkbox
                  size="small"
                  checked={checked}
                  onChange={() => onChange(checked ? '' : option.value)}
                />
              }
              label={option.label}
              sx={{
                mr: 0.8,
                '& .MuiFormControlLabel-label': { fontSize: '0.82rem' },
              }}
            />
          );
        })}
      </Stack>
    </Stack>
  );

  const renderStatusSequencial = (items, values, onChange, options, prefix) => (
    <Box>
      <Stack spacing={1.5} sx={{ display: { xs: 'flex', md: 'none' } }}>
        {items.map((item, index) => {
          const itemKey = `${prefix}_${item}`.replace(/[^a-zA-Z0-9_-]+/g, '_');
          return (
            <Paper key={`mobile-item-${item}`} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Stack spacing={1.2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  {renderCameraButton(itemKey)}
                  <Typography sx={{ fontWeight: 800, fontSize: '0.92rem' }}>
                    {index + 1}. {item}
                  </Typography>
                </Stack>
                {renderMobileOptionButtons(values[item], (value) => onChange(item, value), options)}
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      <Box sx={{ display: { xs: 'none', md: 'block' }, overflow: 'auto', maxHeight: 460, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1 }}>
      <Box
        sx={{
          minWidth: 820,
          display: 'grid',
          gridTemplateColumns: `72px minmax(300px, 2fr) repeat(${options.length}, minmax(140px, 1fr))`,
          columnGap: 1,
          rowGap: 0.25,
          alignItems: 'center',
        }}
      >
        <Box sx={{ position: 'sticky', top: 0, zIndex: 3, bgcolor: 'background.paper' }} />
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '0.82rem',
            position: 'sticky',
            top: 0,
            zIndex: 3,
            bgcolor: 'background.paper',
            py: 0.5,
          }}
        >
          Item
        </Typography>
        {options.map((option) => (
          <Typography
            key={`head-${option.value}`}
            sx={{
              fontWeight: 700,
              fontSize: '0.82rem',
              position: 'sticky',
              top: 0,
              zIndex: 3,
              bgcolor: 'background.paper',
              py: 0.5,
            }}
          >
            {option.label}
          </Typography>
        ))}

        {items.map((item, index) => {
          const itemKey = `${prefix}_${item}`.replace(/[^a-zA-Z0-9_-]+/g, '_');
          return (
          <React.Fragment key={`seq-${item}`}>
            {renderCameraButton(itemKey)}
            <Typography sx={{ fontSize: '0.9rem', pr: 1 }}>{index + 1}. {item}:</Typography>
            {options.map((option) => {
              const checked = values[item] === option.value;
              return (
                <FormControlLabel
                  key={`${item}-${option.value}`}
                  control={
                    <Checkbox
                      size="small"
                      checked={checked}
                      onChange={() => onChange(item, checked ? '' : option.value)}
                    />
                  }
                  label={option.label}
                  sx={{
                    mr: 0,
                    '& .MuiFormControlLabel-label': { fontSize: '0.8rem' },
                  }}
                />
              );
            })}
          </React.Fragment>
        );
        })}
      </Box>
      </Box>
    </Box>
  );

  const tabStats = useMemo(() => {
    const refrigeracao = countFilledValues(detalhado.refrigeracao) + (detalhado.observacoes.refrigeracao ? 1 : 0);
    const ar = countFilledValues(detalhado.ar_condicionado) + (detalhado.observacoes.ar_condicionado ? 1 : 0);
    const gerador = countFilledValues(detalhado.gerador) + (detalhado.observacoes.gerador ? 1 : 0);
    const portas =
      countFilledValues(detalhado.portas_rapidas.resfriada) +
      countFilledValues(detalhado.portas_rapidas.congelada) +
      (detalhado.observacoes.portas_resfriada ? 1 : 0) +
      (detalhado.observacoes.portas_congelada ? 1 : 0);
    const finalizacao = fotos.length + countFilledValues(itemFotos) + (observacoesGerais ? 1 : 0);

    return { refrigeracao, ar, gerador, portas, finalizacao };
  }, [detalhado, fotos.length, itemFotos, observacoesGerais]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
        <Link underline="hover" color="inherit" onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
          <Home sx={{ mr: 0.5 }} fontSize="inherit" /> Dashboard
        </Link>
        <Link
          underline="hover"
          color="inherit"
          onClick={() => navigate('/testes-loja-menu')}
          sx={{ cursor: 'pointer' }}
        >
          Testes de Loja
        </Link>
        <Typography color="text.primary">TESTES (check list)</Typography>
      </Breadcrumbs>

      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
            TESTES (check list)
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Formulário completo conforme o modelo enviado: Refrigeração, Ar Condicionado, Gerador e Portas Rápidas.
          </Typography>

          <Grid container spacing={2.5} alignItems="center">
            <Grid item xs={12} md="auto" sx={{ flexBasis: { xs: '100%' } }}>
              <TextField
                select
                label="Cliente"
                value={clienteId}
                onChange={(e) => handleClienteChange(e.target.value)}
                required
                fullWidth
                sx={{ width: { xs: '100%', md: 330 }, maxWidth: { xs: 330, md: 330 } }}
              >
                <MenuItem value="">Selecione...</MenuItem>
                {clientes.map((cliente) => (
                  <MenuItem key={cliente.id} value={String(cliente.id)}>
                    {cliente.nome}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md="auto">
              <TextField
                label="Data e hora do checklist"
                type="datetime-local"
                value={dataChecklist}
                onChange={(e) => setDataChecklist(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: '100%', sm: 220 } }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Tabs
              value={tabAtiva}
              onChange={(_, value) => setTabAtiva(value)}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label={`Refrigeração (${tabStats.refrigeracao})`} />
              <Tab label={`Ar Condicionado (${tabStats.ar})`} />
              <Tab label={`Gerador (${tabStats.gerador})`} />
              <Tab label={`Portas Rápidas (${tabStats.portas})`} />
              <Tab label={`Finalização (${tabStats.finalizacao})`} />
            </Tabs>
          </Box>

          {tabAtiva === 0 ? (
            <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
              Refrigeração
            </Typography>
            {renderStatusSequencial(
              REFRIGERACAO_ITEMS,
              detalhado.refrigeracao,
              handleRefrigeracaoChange,
              STATUS_TRIPLA,
              'refrigeracao'
            )}
            <TextField
              sx={{ mt: 1.5 }}
              label="Observações - Refrigeração"
              multiline
              minRows={2}
              value={detalhado.observacoes.refrigeracao}
              onChange={(e) => handleObservacaoSecaoChange('refrigeracao', e.target.value)}
            />
            </Paper>
          ) : null}

          {tabAtiva === 1 ? (
            <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
              Ar Condicionado
            </Typography>
            {renderStatusSequencial(
              AR_CONDICIONADO_ITEMS,
              detalhado.ar_condicionado,
              handleArChange,
              STATUS_TRIPLA,
              'ar_condicionado'
            )}
            <TextField
              sx={{ mt: 1.5 }}
              label="Observações - Ar Condicionado"
              multiline
              minRows={2}
              value={detalhado.observacoes.ar_condicionado}
              onChange={(e) => handleObservacaoSecaoChange('ar_condicionado', e.target.value)}
            />
            </Paper>
          ) : null}

          {tabAtiva === 2 ? (
            <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
              Gerador
            </Typography>
            <Stack spacing={0.4}>
              {renderStatusCheckbox('1. Tipo de teste', detalhado.gerador.tipo_teste, (v) => handleGeradorChange('tipo_teste', v), [
                { value: 'com_carga', label: 'COM CARGA' },
                { value: 'sem_carga', label: 'SEM CARGA' },
              ], 'gerador_tipo_teste')}
              {renderStatusCheckbox('2. Partida', detalhado.gerador.partida, (v) => handleGeradorChange('partida', v), [
                { value: 'ok', label: 'OK' },
                { value: 'nao_partiu', label: 'NÃO PARTIU' },
              ], 'gerador_partida')}
              {renderStatusCheckbox('3. Ruído', detalhado.gerador.ruido, (v) => handleGeradorChange('ruido', v), [
                { value: 'ok', label: 'OK' },
                { value: 'ruido_anormal', label: 'RUÍDO ANORMAL' },
              ], 'gerador_ruido')}
            </Stack>
            <Grid container spacing={1.2} sx={{ mt: 0.2 }}>
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={1} alignItems="center">
                  {renderCameraButton('gerador_nivel_comb_tanque')}
                  <TextField
                    size="small"
                    label="4. Nível combustível no tanque (litros)"
                    value={detalhado.gerador.nivel_comb_tanque}
                    onChange={(e) => handleGeradorChange('nivel_comb_tanque', e.target.value)}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={1} alignItems="center">
                  {renderCameraButton('gerador_nivel_comb_reservatorio')}
                  <TextField
                    size="small"
                    label="5. Nível combustível no reservatório (litros)"
                    value={detalhado.gerador.nivel_comb_reservatorio}
                    onChange={(e) => handleGeradorChange('nivel_comb_reservatorio', e.target.value)}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={1} alignItems="center">
                  {renderCameraButton('gerador_nivel_agua')}
                  <TextField
                    size="small"
                    label="6. Nível de água"
                    value={detalhado.gerador.nivel_agua}
                    onChange={(e) => handleGeradorChange('nivel_agua', e.target.value)}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                {renderStatusCheckbox('7. Nível de óleo do motor', detalhado.gerador.nivel_oleo_motor, (v) => handleGeradorChange('nivel_oleo_motor', v), [
                  { value: 'ok', label: 'OK' },
                  { value: 'medio', label: 'MÉDIO' },
                  { value: 'baixo', label: 'BAIXO' },
                ], 'gerador_nivel_oleo_motor')}
              </Grid>
            </Grid>
            <TextField
              sx={{ mt: 1.5 }}
              label="Observações - Gerador"
              multiline
              minRows={2}
              value={detalhado.observacoes.gerador}
              onChange={(e) => handleObservacaoSecaoChange('gerador', e.target.value)}
            />
            </Paper>
          ) : null}

          {tabAtiva === 3 ? (
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Portas Rápidas - Câmara Resfriada
                </Typography>
                <Stack spacing={0.4}>
                  {PORTA_FIELDS.map((field, index) => (
                    <Box key={`resfriada-${field.key}`}>
                      {renderStatusCheckbox(
                        `${index + 1}. ${field.label}`,
                        detalhado.portas_rapidas.resfriada[field.key],
                        (value) => handlePortaChange('resfriada', field.key, value),
                        field.options,
                        `portas_resfriada_${field.key}`
                      )}
                    </Box>
                  ))}
                </Stack>
                <TextField
                  sx={{ mt: 1.5 }}
                  label="Observações - Porta Resfriada"
                  multiline
                  minRows={2}
                  value={detalhado.observacoes.portas_resfriada}
                  onChange={(e) => handleObservacaoSecaoChange('portas_resfriada', e.target.value)}
                />
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Portas Rápidas - Câmara Congelada
                </Typography>
                <Stack spacing={0.4}>
                  {PORTA_FIELDS.map((field, index) => (
                    <Box key={`congelada-${field.key}`}>
                      {renderStatusCheckbox(
                        `${index + 1}. ${field.label}`,
                        detalhado.portas_rapidas.congelada[field.key],
                        (value) => handlePortaChange('congelada', field.key, value),
                        field.options,
                        `portas_congelada_${field.key}`
                      )}
                    </Box>
                  ))}
                </Stack>
                <TextField
                  sx={{ mt: 1.5 }}
                  label="Observações - Porta Congelada"
                  multiline
                  minRows={2}
                  value={detalhado.observacoes.portas_congelada}
                  onChange={(e) => handleObservacaoSecaoChange('portas_congelada', e.target.value)}
                />
              </Paper>
            </Stack>
          ) : null}

          {tabAtiva === 4 ? (
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Fotos anexadas
                </Typography>
                <input
                  ref={fotoInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFotosChange}
                />
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    variant="outlined"
                    startIcon={<PhotoCamera />}
                    onClick={() => fotoInputRef.current?.click()}
                    disabled={fotos.length >= 10}
                  >
                    Selecionar Fotos
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    {fotos.length}/10
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
                  {fotos.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Nenhuma foto selecionada.
                    </Typography>
                  ) : (
                    fotos.map((file, index) => (
                      <Chip
                        key={`${file.name}-${index}`}
                        label={file.name}
                        size="small"
                        onDelete={() => removerFoto(index)}
                      />
                    ))
                  )}
                </Stack>
              </Paper>

              <TextField
                label="Observações gerais"
                multiline
                minRows={4}
                value={observacoesGerais}
                onChange={(e) => setObservacoesGerais(e.target.value)}
                placeholder="Ocorrências gerais do checklist."
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button variant="contained" startIcon={<Save />} onClick={handleSalvar} disabled={saving}>
                  {saving ? 'Salvando...' : 'Salvar checklist'}
                </Button>
                <Button variant="outlined" startIcon={<Visibility />} onClick={() => navigate('/testes-checklist/consulta')}>
                  Consultar checklists
                </Button>
              </Stack>
            </Stack>
          ) : null}

          {feedback.message ? (
            <Alert severity={feedback.type || 'info'} sx={{ mt: 2.5 }}>
              {feedback.message}
            </Alert>
          ) : null}

          <Stack direction="row" spacing={1} sx={{ mt: 2, justifyContent: 'space-between' }}>
            <Button
              variant="text"
              disabled={tabAtiva === 0}
              onClick={() => setTabAtiva((prev) => Math.max(0, prev - 1))}
            >
              Aba anterior
            </Button>
            <Button
              variant="text"
              disabled={tabAtiva === 4}
              onClick={() => setTabAtiva((prev) => Math.min(4, prev + 1))}
            >
              Próxima aba
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
